import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Brain, Play, Loader2, RefreshCw, Mail, Table2 } from "lucide-react";
import ConversationsTab from "./LearningConversations";

const TYPE_LABELS = {
  hotel: { label: "Hotel / Alojamiento", emoji: "🏨", color: "border-amber-300 bg-amber-50/40" },
  transfer: { label: "Transfer / Taxi", emoji: "🚐", color: "border-blue-300 bg-blue-50/40" },
  activity: { label: "Actividad / Tour", emoji: "🎟️", color: "border-emerald-300 bg-emerald-50/40" },
  restaurant: { label: "Restaurante / Comida", emoji: "🍽️", color: "border-orange-300 bg-orange-50/40" },
  train: { label: "Tren", emoji: "🚆", color: "border-purple-300 bg-purple-50/40" },
  flight: { label: "Vuelo", emoji: "✈️", color: "border-sky-300 bg-sky-50/40" },
  rental_car: { label: "Alquiler de coche", emoji: "🚗", color: "border-indigo-300 bg-indigo-50/40" },
  other: { label: "Otros / Día libre", emoji: "📌", color: "border-gray-300 bg-gray-50/40" },
};

const KIND_BADGES = {
  date: "bg-purple-100 text-purple-800",
  time: "bg-blue-100 text-blue-800",
  money: "bg-emerald-100 text-emerald-800",
  number: "bg-cyan-100 text-cyan-800",
  text: "bg-gray-100 text-gray-700",
};

export default function LearningPage() {
  const [tab, setTab] = useState("schemas");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runStatus, setRunStatus] = useState({ status: "idle" });
  const [polling, setPolling] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/learning/schemas");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar esquemas");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/learning/status");
      setRunStatus(data);
      if (data.status !== "running") {
        setPolling(false);
        if (data.status === "done") {
          toast.success("Análisis completado");
          fetchData();
        } else if (data.status === "error") {
          toast.error(`Análisis falló: ${data.error}`);
        }
      }
    } catch (err) {
      // silent — polling will retry
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        const { data: schemasData } = await api.get("/learning/schemas");
        setData(schemasData);
      } catch (e) {
        // initial fetch can silently fail; user can press Refrescar
      }
      try {
        const { data: stData } = await api.get("/learning/status");
        setRunStatus(stData);
      } catch (e) {
        // ignore
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!polling) return undefined;
    const id = setInterval(() => { fetchStatus(); }, 5000);
    return () => clearInterval(id);
  }, [polling]);

  const startAnalysis = async () => {
    if (!confirm("Esto recorrerá ~25 trips Cerrados en gestion (≈5-8 minutos de scraping). ¿Continuar?")) return;
    try {
      await api.post("/learning/run", { sample_trips: 25, bookings_per_trip: 8 });
      toast.info("Análisis iniciado — tarda 5-8 minutos");
      setPolling(true);
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar");
    }
  };

  const isEmpty = data?.empty;
  const schemas = data?.schemas || {};

  return (
    <div className="space-y-6" data-testid="learning-page">
      <div>
        <p className="vv-label">Etapa 2 — Aprendizaje</p>
        <h1 className="text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2">
          <Brain size={28} className="text-purple-600" />
          Aprendizaje de Reservas
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setTab("schemas")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "schemas" ? "border-purple-600 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          data-testid="learning-tab-schemas"
        >
          <Table2 size={14} /> Esquemas de reservas
        </button>
        <button
          onClick={() => setTab("conversations")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "conversations" ? "border-purple-600 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          data-testid="learning-tab-conversations"
        >
          <Mail size={14} /> Conversaciones (Heidi)
        </button>
      </div>

      {tab === "conversations" && <ConversationsTab />}

      {tab === "schemas" && (
      <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500 max-w-2xl">
          Recorremos trips Cerrados en gestion.viajadverdad.com y aprendemos qué campos rellena cada
          tipo de proveedor. Esto define el esquema que el LLM usará en la siguiente fase para extraer
          datos de las respuestas por correo.
        </p>
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="learning-refresh">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
          <Button
            onClick={startAnalysis}
            disabled={runStatus.status === "running"}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="learning-run"
          >
            {runStatus.status === "running" ? (
              <><Loader2 size={14} className="mr-1 animate-spin" /> Analizando…</>
            ) : (
              <><Play size={14} className="mr-1" /> Iniciar análisis</>
            )}
          </Button>
        </div>
      </div>

      {/* Status banner */}
      {runStatus.status === "running" && (
        <div className="vv-card p-4 border-l-4 border-purple-500 bg-purple-50/50 flex items-center gap-3">
          <Loader2 size={20} className="animate-spin text-purple-600" />
          <div>
            <div className="font-medium text-purple-900">Analizando trips cerrados…</div>
            <div className="text-xs text-purple-700">Iniciado: {runStatus.started_at}. Tarda unos 5-8 minutos. Puedes cerrar esta página, el proceso continúa en background.</div>
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="vv-card p-12 text-center text-gray-500">
          <Brain size={48} className="mx-auto mb-3 text-gray-300" />
          <p>Aún no se ha ejecutado el análisis. Pulsa <b>&quot;Iniciar análisis&quot;</b> arriba.</p>
        </div>
      ) : (
        <>
          <div className="vv-card p-4 text-sm flex flex-wrap gap-6">
            <Stat label="Trips analizados" value={data?.trips_processed} />
            <Stat label="Reservas inspeccionadas" value={data?.total_bookings_inspected} />
            <Stat label="Categorías detectadas" value={Object.keys(schemas).length} />
            <Stat label="Snapshot" value={data?.created_at?.slice(0, 16).replace("T", " ") || "—"} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(schemas)
              .sort((a, b) => b[1].booking_count - a[1].booking_count)
              .map(([stype, schema]) => {
                const meta = TYPE_LABELS[stype] || { label: stype, emoji: "📄", color: "border-gray-300" };
                return (
                  <div key={stype} className={`vv-card border-2 ${meta.color}`} data-testid={`schema-${stype}`}>
                    <div className="p-4 border-b border-gray-200/60 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{meta.emoji}</span>
                        <div>
                          <div className="font-semibold">{meta.label}</div>
                          <div className="text-[11px] text-gray-500 uppercase tracking-wider">{stype}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-semibold">{schema.booking_count}</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">muestras</div>
                      </div>
                    </div>

                    <div className="p-4 space-y-3">
                      <FieldSection title="Campos siempre presentes" fields={schema.required_fields} sectionType="required" />
                      {schema.optional_fields?.length > 0 && (
                        <FieldSection title="Campos ocasionales (20-70%)" fields={schema.optional_fields.slice(0, 6)} sectionType="optional" />
                      )}
                      {schema.top_providers?.length > 0 && (
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Top proveedores</div>
                          <div className="flex flex-wrap gap-1.5">
                            {schema.top_providers.slice(0, 5).map((p) => (
                              <span key={p.name} className="text-[11px] bg-white border border-gray-200 rounded px-2 py-0.5">
                                {p.name.split(" - ")[0]} <span className="text-gray-400">×{p.n}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

function FieldSection({ title, fields, sectionType }) {
  if (!fields?.length) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">{title}</div>
      <div className="space-y-1">
        {fields.map((f) => (
          <div key={f.field} className="flex items-start gap-2 text-xs">
            <span className={`shrink-0 ${KIND_BADGES[f.kind] || KIND_BADGES.text} px-1.5 py-0.5 rounded text-[10px] font-medium uppercase`}>{f.kind}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-mono font-medium text-gray-800">{f.field}</span>
                {f.key && (
                  <span className="text-[9px] bg-amber-100 border border-amber-300 text-amber-800 rounded px-1 py-px font-semibold uppercase tracking-wide">clave</span>
                )}
                <span className="text-gray-400 text-[10px]">{f.presence_pct}%</span>
              </div>
              {f.examples?.length > 0 && (
                <div className="text-gray-500 text-[11px] truncate" title={f.examples.join(" | ")}>
                  ej: {f.examples[0]?.slice(0, 70)}
                  {f.examples[0]?.length > 70 ? "…" : ""}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
